import React from "react";
import { useFormik } from "formik";
import {
  Grid,
  TextField,
  MenuItem,
  Alert,
  Box,
  Typography,
  InputAdornment,
} from "@mui/material";
import { FormDialog } from "../../../components/common/FormDialog/FormDialog";
import {
  adjustBalanceValidationSchema,
  AdjustBalanceFormValues,
} from "../validation/adjustBalanceValidationSchema";
import { useAdjustEmployeeBalanceMutation } from "../../../api/absenceBalancesApi";
import { EmployeeAbsenceBalanceDto } from "../../../types/absence";
import {
  AbsenceCategory,
  AbsenceCategoryNames,
} from "../../../constants/absenceEnums";
import { extractAllErrors } from "../../../utils/errorUtils";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";

interface AdjustBalanceDialogProps {
  open: boolean;
  employee: EmployeeAbsenceBalanceDto | null;
  onClose: () => void;
  onSuccess?: () => void;
}

const AdjustBalanceDialog: React.FC<AdjustBalanceDialogProps> = ({
  open,
  employee,
  onClose,
  onSuccess,
}) => {
  const [adjustBalance, { isLoading, error }] =
    useAdjustEmployeeBalanceMutation();

  const formik = useFormik<AdjustBalanceFormValues>({
    initialValues: {
      absenceCategory: null,
      amount: 0,
      reason: "",
    },
    validationSchema: adjustBalanceValidationSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      if (!employee) return;

      try {
        await adjustBalance({
          employeeId: employee.employeeId,
          dto: {
            absenceCategory: values.absenceCategory!,
            amount: values.amount,
            reason: values.reason,
          },
        }).unwrap();

        formik.resetForm();
        onClose();
        onSuccess?.();
      } catch (err) {
        console.error("Failed to adjust balance:", err);
      }
    },
  });

  const handleClose = () => {
    formik.resetForm();
    onClose();
  };

  if (!employee) return null;

  const currentBalance =
    formik.values.absenceCategory === AbsenceCategory.Vacation
      ? employee.vacationDaysAvailable
      : formik.values.absenceCategory === AbsenceCategory.SickDays
      ? employee.sickDaysAvailable
      : 0;

  const newBalance = currentBalance + formik.values.amount;

  return (
    <FormDialog
      open={open}
      title={`Adjust Balance - ${employee.employeeName}`}
      onClose={handleClose}
      onSubmit={formik.handleSubmit}
      isLoading={isLoading}
      submitLabel="Adjust Balance"
      maxWidth="sm"
    >
      <Grid container spacing={3}>
        {/* Error Alert */}
        {error && (
          <Grid item xs={12}>
            <Alert severity="error">
              <Box>
                {extractAllErrors(error).map((err, index) => (
                  <Box key={index}>• {err}</Box>
                ))}
              </Box>
            </Alert>
          </Grid>
        )}

        {/* Absence Category */}
        <Grid item xs={12}>
          <TextField
            select
            fullWidth
            label="Absence Type"
            name="absenceCategory"
            value={formik.values.absenceCategory || ""}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={
              formik.touched.absenceCategory &&
              Boolean(formik.errors.absenceCategory)
            }
            helperText={
              formik.touched.absenceCategory && formik.errors.absenceCategory
            }
            required
            size="small"
          >
            <MenuItem value={AbsenceCategory.Vacation}>
              {AbsenceCategoryNames[AbsenceCategory.Vacation]}
            </MenuItem>
            <MenuItem value={AbsenceCategory.SickDays}>
              {AbsenceCategoryNames[AbsenceCategory.SickDays]}
            </MenuItem>
          </TextField>
        </Grid>

        {/* Current Balance Display */}
        {formik.values.absenceCategory && (
          <Grid item xs={12}>
            <Box
              sx={{
                p: 2,
                backgroundColor: "info.light",
                borderRadius: 1,
                color: "info.contrastText",
              }}
            >
              <Typography variant="body2" fontWeight={500}>
                Current Balance: {currentBalance} days
              </Typography>
            </Box>
          </Grid>
        )}

        {/* Amount */}
        <Grid item xs={12}>
          <TextField
            fullWidth
            type="number"
            label="Amount"
            name="amount"
            value={formik.values.amount}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.amount && Boolean(formik.errors.amount)}
            helperText={
              formik.touched.amount && formik.errors.amount
                ? formik.errors.amount
                : "Use positive to add days, negative to subtract days"
            }
            required
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  {formik.values.amount >= 0 ? (
                    <AddIcon fontSize="small" color="success" />
                  ) : (
                    <RemoveIcon fontSize="small" color="error" />
                  )}
                </InputAdornment>
              ),
            }}
          />
        </Grid>

        {/* New Balance Preview */}
        {formik.values.absenceCategory && formik.values.amount !== 0 && (
          <Grid item xs={12}>
            <Box
              sx={{
                p: 2,
                backgroundColor:
                  newBalance < 0
                    ? "error.light"
                    : formik.values.amount > 0
                    ? "success.light"
                    : "warning.light",
                borderRadius: 1,
                color:
                  newBalance < 0
                    ? "error.contrastText"
                    : formik.values.amount > 0
                    ? "success.contrastText"
                    : "warning.contrastText",
              }}
            >
              <Typography variant="body2" fontWeight={500}>
                New Balance: {newBalance} days
                {newBalance < 0 && (
                  <Typography
                    component="span"
                    variant="caption"
                    sx={{ ml: 1, fontWeight: 400 }}
                  >
                    (Warning: Negative balance)
                  </Typography>
                )}
              </Typography>
            </Box>
          </Grid>
        )}

        {/* Reason */}
        <Grid item xs={12}>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Reason"
            name="reason"
            value={formik.values.reason}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.reason && Boolean(formik.errors.reason)}
            helperText={formik.touched.reason && formik.errors.reason}
            required
            size="small"
            placeholder="Please provide a reason for this adjustment..."
          />
        </Grid>
      </Grid>
    </FormDialog>
  );
};

export default AdjustBalanceDialog;






